import { defineStore } from 'pinia'
import { ref } from 'vue'
import { Bike, PhotoList, type Photo, type PhotoOwnerType, type PhotoTag } from '@klunkers/shared'
import { api } from '@/api/http'
import { uploadPhoto } from '@/api/uploads'
import { useGarageStore } from './garage'

const ownerKey = (type: PhotoOwnerType, id: string) => `${type}:${id}`

/** Photos by owner. Refreshed live when media-agent finishes processing. */
export const usePhotosStore = defineStore('photos', () => {
  const byOwner = ref<Record<string, Photo[]>>({})
  const uploading = ref<Record<string, number>>({})

  const list = (type: PhotoOwnerType, id: string) => byOwner.value[ownerKey(type, id)] ?? []

  async function load(type: PhotoOwnerType, id: string) {
    const { photos } = await api(`/photos?owner_type=${type}&owner_id=${id}`, { schema: PhotoList })
    byOwner.value = { ...byOwner.value, [ownerKey(type, id)]: photos }
    return photos
  }

  /**
   * The live "processed" event normally updates a new photo. If it is missed
   * (the connection dropped), ask the API a few times instead, rather than
   * leave it saying "Processing…" until the page is reloaded.
   */
  async function watchUntilProcessed(type: PhotoOwnerType, id: string, photoId: string) {
    const pending = () => list(type, id).find((p) => p.id === photoId)?.state === 'pending'
    for (const wait of [4_000, 12_000, 30_000, 60_000]) {
      await new Promise((r) => setTimeout(r, wait))
      if (!pending()) return // the live event got there first
      await load(type, id).catch(() => {})
      if (!pending()) {
        // A bike's main-photo thumbnail may be ready now too.
        if (type === 'bike')
          await useGarageStore()
            .load()
            .catch(() => {})
        return
      }
    }
  }

  async function add(type: PhotoOwnerType, id: string, blob: Blob, tag: PhotoTag = 'general') {
    const k = ownerKey(type, id)
    uploading.value = { ...uploading.value, [k]: (uploading.value[k] ?? 0) + 1 }
    try {
      const photo = await uploadPhoto(blob, { owner_type: type, owner_id: id, tag })
      byOwner.value = {
        ...byOwner.value,
        [k]: [photo, ...list(type, id).filter((p) => p.id !== photo.id)],
      }
      if (photo.state === 'pending') void watchUntilProcessed(type, id, photo.id)
      return photo
    } finally {
      uploading.value = { ...uploading.value, [k]: Math.max(0, (uploading.value[k] ?? 1) - 1) }
    }
  }

  async function remove(photo: Photo) {
    await api(`/photos/${photo.id}`, { method: 'DELETE' })
    await load(photo.owner_type, photo.owner_id)
  }

  async function setHero(bikeId: string, photoId: string | null) {
    const bike = await api(`/bikes/${bikeId}/hero`, {
      method: 'PUT',
      body: { photo_id: photoId },
      schema: Bike,
    })
    const garage = useGarageStore()
    garage.bikes = garage.bikes.map((b) => (b.id === bike.id ? bike : b))
  }

  /** Called on the socket's photo.processed event. */
  async function processed(payload: { owner_type: string; owner_id: string }) {
    const type = payload.owner_type as PhotoOwnerType
    if (byOwner.value[ownerKey(type, payload.owner_id)]) await load(type, payload.owner_id)
    // A bike's hero thumbnail may have just become available.
    if (type === 'bike')
      await useGarageStore()
        .load()
        .catch(() => {})
  }

  return { byOwner, uploading, list, load, add, remove, setHero, processed }
})
